import type { Config } from 'dompurify';

type SanitizeFn = (dirty: string, config?: Config) => string;

let cachedSanitize: SanitizeFn | null = null;
let pendingImport: Promise<SanitizeFn> | null = null;

async function load(): Promise<SanitizeFn> {
  if (cachedSanitize) return cachedSanitize;
  if (!pendingImport) {
    pendingImport = import('dompurify').then((m) => {
      const dp = m.default || (m as any);
      // Add hook once (mirrors previous inline usage pattern)
      try {
        dp.addHook('afterSanitizeAttributes', (node: Element) => {
          if ('target' in node) {
            node.setAttribute('target', '_blank');
            node.setAttribute('rel', 'noopener noreferrer');
          }
          // Fix links that lack a protocol (e.g., www.example.com or example.com)
          if (node.tagName === 'A') {
            const href = node.getAttribute('href');
            if (href && !/^https?:\/\//i.test(href)) {
              node.setAttribute('href', `https://${href}`);
            }
          }
        });
      } catch {
        /* noop */
      }
      cachedSanitize = dp.sanitize.bind(dp);
      return cachedSanitize!;
    });
  }
  return pendingImport;
}

export async function sanitizeHtml(html: string, config?: Config): Promise<string> {
  const sanitize = await load();
  return sanitize(html, config);
}

// Synchronous convenience API (returns undefined until module loaded)
export function trySanitizeHtml(html: string, config?: Config): string | undefined {
  if (!cachedSanitize) return undefined;
  return cachedSanitize(html, config);
}

// Optional prefetch (e.g., call in idle callback)
export function prefetchSanitizer() {
  void load();
}
